import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '../components/icons';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import memberService from '../services/memberService';
import api from '../services/api';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(6),
  },
  borderRadius: '24px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
  border: '1px solid rgba(102, 126, 234, 0.08)',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  maxWidth: 600,
  width: '100%',
  margin: 'auto'
}));

const AcceptInvitationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, currentUser, fetchUserData } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [userCreated, setUserCreated] = useState(false);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [acceptedData, setAcceptedData] = useState(null);

  // Password setup state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);

  const acceptAttempted = useRef(false);

  useEffect(() => {
    loadInvitation();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      invitation &&
      isAuthenticated() &&
      !accepting &&
      !success &&
      !showPasswordSetup &&
      !error &&
      !acceptAttempted.current
    ) {
      acceptAttempted.current = true;
      handleAccept();
    }
  }, [invitation, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInvitation = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await memberService.getInvitationByToken(token);
      setInvitation(response.data);
    } catch (err) {
      console.error('Error loading invitation:', err);
      setError(err.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    // If user is authenticated but email doesn't match, show error
    if (isAuthenticated() && currentUser?.email !== invitation?.email) {
      setError(`This invitation was sent to ${invitation?.email}. Please log in with that email address.`);
      return;
    }

    try {
      setAccepting(true);
      setError(null);

      const response = await memberService.acceptInvitation(token);

      // Check if user needs to set up password
      if (response.data.requires_password_setup) {
        setUserCreated(true);
        setAcceptedData(response.data);
        setShowPasswordSetup(true);
      } else {
        setSuccess(true);

        try {
          await fetchUserData();
        } catch (fetchError) {
          // Don't block the redirect if refreshing user data fails
        }

        setTimeout(() => {
          navigate('/admin');
        }, 2000);
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);

      // Handle specific error cases
      if (err.response?.status === 401 || err.response?.status === 403) {
        const responseData = err.response?.data;
        if (responseData?.requires_login && responseData?.user_exists) {
          // Account exists but user needs to log in
          // Redirect to login page with return URL
          navigate(`/login?redirect=/accept-invitation/${token}`, {
            state: {
              message: `Please log in with ${responseData.email} to accept this invitation to ${responseData.merchant?.name}.`
            }
          });
          return;
        }
      }

      setError(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleSetupPassword = async (e) => {
    e.preventDefault();

    if (!firstName || !lastName || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setSettingPassword(true);
      setError(null);

      console.log('Setting up password...');
      const setupResponse = await api.post('/users/auth/setup-password', {
        invitation_token: acceptedData.invitation_token || token,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password: password,
        confirm_password: confirmPassword
      });

      console.log('Password setup successful:', setupResponse.data);

      // Fetch user data to establish session
      console.log('Fetching user data...');
      try {
        await fetchUserData();
        console.log('User data fetched successfully');
      } catch (fetchError) {
        console.error('Error fetching user data:', fetchError);
        // Don't fail the entire flow if fetchUserData fails
        // The user is already logged in on the backend
      }

      setSuccess(true);

      // Redirect to dashboard after showing success message
      setTimeout(() => {
        navigate('/admin');
      }, 2000);
    } catch (err) {
      console.error('Error setting up password:', err);
      console.error('Error details:', err.response?.data);
      console.error('Error status:', err.response?.status);
      setError(err.response?.data?.detail || err.message || 'Failed to set up password');
    } finally {
      setSettingPassword(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 } }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="textSecondary">
            Loading invitation...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && !invitation) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 } }}>
        <div>
          <StyledPaper>
            <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
              <Avatar sx={{ width: { xs: 60, sm: 80 }, height: { xs: 60, sm: 80 }, bgcolor: 'error.main' }}>
                <ErrorIcon sx={{ fontSize: { xs: 40, sm: 50 } }} />
              </Avatar>

              <Typography variant="h4" fontWeight={700} textAlign="center">
                Invalid Invitation
              </Typography>

              <Typography variant="body1" color="textSecondary" textAlign="center">
                {error}
              </Typography>

              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  mt: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4
                }}
              >
                Go to Home
              </Button>
            </Box>
          </StyledPaper>
        </div>
      </Container>
    );
  }

  // Password Setup Screen
  if (showPasswordSetup && acceptedData) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 } }}>
        <div>
          <StyledPaper>
            <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
              <Avatar sx={{ width: { xs: 60, sm: 80 }, height: { xs: 60, sm: 80 }, bgcolor: '#667eea' }}>
                <LockIcon sx={{ fontSize: { xs: 40, sm: 50 } }} />
              </Avatar>

              <Typography variant="h4" fontWeight={700} textAlign="center">
                Set Up Your Password
              </Typography>

              <Typography variant="body1" color="textSecondary" textAlign="center">
                Welcome! You've been invited to join {acceptedData.merchant.name}.
                Please create a password to secure your account.
              </Typography>

              <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mt: 1 }}>
                Account email: {acceptedData.email}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%' }}>
                  {error}
                </Alert>
              )}

              <Box
                component="form"
                onSubmit={handleSetupPassword}
                sx={{ width: '100%', mt: 2 }}
              >
                <TextField
                  fullWidth
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter your first name"
                  sx={{ mb: 3 }}
                  required
                />

                <TextField
                  fullWidth
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter your last name"
                  sx={{ mb: 3 }}
                  required
                />

                <Divider sx={{ mb: 3 }} />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  sx={{ mb: 3 }}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Alert severity="info" sx={{ mb: 3 }}>
                  Password must be at least 8 characters long
                </Alert>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={settingPassword}
                  sx={{
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5,
                    fontSize: '1.1rem'
                  }}
                >
                  {settingPassword ? 'Setting up...' : 'Complete Setup'}
                </Button>
              </Box>
            </Box>
          </StyledPaper>
        </div>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 } }}>
        <div>
          <StyledPaper>
            <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
              <Avatar sx={{ width: { xs: 60, sm: 80 }, height: { xs: 60, sm: 80 }, bgcolor: 'success.main' }}>
                <CheckCircleIcon sx={{ fontSize: { xs: 40, sm: 50 } }} />
              </Avatar>

              <Typography variant="h4" fontWeight={700} textAlign="center">
                {userCreated ? 'Welcome Aboard!' : 'Welcome to the Team!'}
              </Typography>

              <Typography variant="body1" color="textSecondary" textAlign="center">
                {userCreated
                  ? `Your account is all set! You've joined ${acceptedData?.merchant?.name || invitation?.merchant_name}.`
                  : `You've successfully joined ${invitation?.merchant_name}.`
                }
              </Typography>

              <Typography variant="body2" color="textSecondary">
                Redirecting to dashboard...
              </Typography>

              <CircularProgress size={40} />
            </Box>
          </StyledPaper>
        </div>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 } }}>
      <div>
        <StyledPaper>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            {/* Merchant Logo or Icon */}
            {invitation?.merchant_logo ? (
              <Avatar
                src={invitation.merchant_logo}
                sx={{ width: 100, height: 100 }}
              />
            ) : (
              <Avatar sx={{ width: 100, height: 100, bgcolor: '#667eea' }}>
                <BusinessIcon sx={{ fontSize: 60 }} />
              </Avatar>
            )}

            {/* Title */}
            <Typography variant="h4" fontWeight={700} textAlign="center">
              You're Invited!
            </Typography>

            {/* Invitation Details */}
            <Box sx={{ width: '100%', my: 2 }}>
              <Typography variant="body1" color="textSecondary" textAlign="center" paragraph>
                You have been invited to join
              </Typography>

              <Typography variant="h5" fontWeight={600} textAlign="center" color="primary" paragraph>
                {invitation?.merchant_name}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  Role:
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                  {invitation?.role}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  Email:
                </Typography>
                <Typography variant="body1">
                  {invitation?.email}
                </Typography>
              </Box>

              {invitation?.is_expired && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  This invitation has expired
                </Alert>
              )}
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}

            {/* Auth Status */}
            {isAuthenticated() && currentUser?.email !== invitation?.email && (
              <Alert severity="warning" sx={{ width: '100%' }}>
                You are logged in as {currentUser?.email}, but this invitation was sent to {invitation?.email}.
                Please log out and try again.
              </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleAccept}
                disabled={accepting || invitation?.is_expired || (isAuthenticated() && currentUser?.email !== invitation?.email)}
                sx={{
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5
                }}
              >
                {accepting ? 'Processing...' : (isAuthenticated() ? 'Accept Invitation' : 'Accept & Create Account')}
              </Button>
            </Box>
          </Box>
        </StyledPaper>
      </div>
    </Container>
  );
};

export default AcceptInvitationPage;

